import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { openPrintableDocument } from '../common/printableDocument'
import { getCustomerTypes, getAllCustomers } from '../services/customer_service'
import {
  getExerciseOrderBooks,
  createExerciseOrder,
  updateExerciseOrder,
  getExerciseOrderById,
} from '../services/exercise_order_service'
import './NewExerciseOrder.css'

const getValue = (record, keys, fallback = '-') =>
  keys
    .map((key) => record?.[key])
    .find((value) => value !== undefined && value !== null && value !== '') ??
  fallback
const getOptionValue = (option) =>
  typeof option === 'object'
    ? getValue(option, ['id', 'value', 'customerid', 'customer_id', 'userid'])
    : option
const getOptionLabel = (option) =>
  typeof option === 'object'
    ? getValue(option, [
        'name',
        'label',
        'cust_name',
        'customer_name',
        'customertype',
        'type',
      ])
    : option

const getBookId = (book) => getValue(book, ['id', 'bookid', 'book_id', 'isbn'])
const getBookTitle = (book) =>
  getValue(book, ['title', 'name', 'book_title', 'bookTitle'])
const getBookAuthor = (book) =>
  getValue(book, ['author', 'author_name', 'authorName'], '')
const getBookUnitCost = (book) =>
  Number(getValue(book, ['unitCost', 'unit_cost', 'price', 'amount'], 0))
const getStockQuantity = (book) =>
  Number(
    getValue(book, ['stock', 'instock', 'stockQuantity', 'stock_quantity'], 0),
  )
const formatCurrency = (value) =>
  new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency: 'GHS',
    maximumFractionDigits: 2,
  }).format(Number(value) || 0)

const getLineTotal = (item) => {
  const gross = Number(item.unitCost) * Number(item.quantity)
  const discount = gross * (Number(item.discount) / 100)
  return gross - discount
}

function NewExerciseOrder() {
  const { orderId } = useParams()
  const isEditing = Boolean(orderId)
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const loginUserId = user?.userid ?? user?.user_id ?? user?.id
  const [customerType, setCustomerType] = useState('')
  const [customerId, setCustomerId] = useState('')
  const [orderDate, setOrderDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  )
  const [bookFilter, setBookFilter] = useState('')
  const [cartItems, setCartItems] = useState([])
  const [customerTypes, setCustomerTypes] = useState([])
  const [customers, setCustomers] = useState([])
  const [books, setBooks] = useState([])
  const [error, setError] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    let isMounted = true

    const initForm = async () => {
      try {
        const loadedCustomerTypes = await getCustomerTypes()
        if (!isMounted) return
        setCustomerTypes(loadedCustomerTypes)

        if (isEditing) {
          let orderData = await getExerciseOrderById(orderId)
          if (!orderData)
            throw new Error(`Order ${orderId} could not be loaded.`)

          const custId = String(
            getValue(
              orderData,
              ['customerid', 'customer_id', 'customerId', 'cust_id'],
              '',
            ),
          )
          const dateVal = getValue(
            orderData,
            ['order_date', 'orderDate', 'date'],
            '',
          )
          setCustomerId(custId)
          if (dateVal) setOrderDate(String(dateVal).slice(0, 10))

          const loadedCustomers = await getAllCustomers('')
          if (!isMounted) return
          setCustomers(loadedCustomers)
          if (custId) {
            const loadedBooks = await getExerciseOrderBooks(custId)
            if (!isMounted) return
            setBooks(loadedBooks)
          }

          const existingBooks = getValue(
            orderData,
            ['orderBooks', 'order_books', 'books', 'items'],
            [],
          )
          if (Array.isArray(existingBooks) && existingBooks.length > 0) {
            setCartItems(
              existingBooks.map((b) => {
                const qty = Number(
                  getValue(b, ['quantity', 'ordered_quantity', 'qty'], 1),
                )
                const cartons = Number(
                  getValue(b, ['cartons'], Math.floor(qty / 200)),
                )
                const packs = Number(
                  getValue(b, ['packs'], Math.floor((qty % 200) / 20)),
                )
                const copies = Number(getValue(b, ['copies'], qty % 20))
                const discountVal = Number(
                  getValue(b, ['discount', 'discount_percent'], 0),
                )
                const discount =
                  discountVal > 0 && discountVal < 1
                    ? discountVal * 100
                    : discountVal
                return {
                  bookId: getValue(b, ['bookid', 'book_id', 'id']),
                  description: getValue(b, [
                    'book_title',
                    'bookTitle',
                    'description',
                    'title',
                    'name',
                  ]),
                  unitCost: Number(
                    getValue(
                      b,
                      [
                        'unitprice',
                        'unitPrice',
                        'unitcost',
                        'unitCost',
                        'price',
                      ],
                      0,
                    ),
                  ),
                  stockQuantity: getStockQuantity(b),
                  cartons,
                  packs,
                  copies,
                  quantity: qty,
                  discount,
                }
              }),
            )
          }
        } else {
          const bookshopType = loadedCustomerTypes.find((type) =>
            String(getOptionLabel(type)).toLowerCase().includes('bookshop'),
          )
          const defaultType = bookshopType ? getOptionValue(bookshopType) : ''
          setCustomerType(defaultType)
          const defaultCustomers = await getAllCustomers(defaultType)
          if (isMounted) setCustomers(defaultCustomers)
        }
      } catch (requestError) {
        if (isMounted) setError(requestError.message)
      }
    }

    initForm()
    return () => {
      isMounted = false
    }
  }, [orderId, isEditing, location.state])

  const filteredBooks = useMemo(() => {
    const term = bookFilter.trim().toLowerCase()
    if (!term) return books
    return books.filter(
      (book) =>
        getBookTitle(book).toLowerCase().includes(term) ||
        getBookAuthor(book).toLowerCase().includes(term),
    )
  }, [bookFilter, books])

  const totals = useMemo(() => {
    const grossTotal = cartItems.reduce(
      (sum, item) => sum + Number(item.unitCost) * Number(item.quantity),
      0,
    )
    const discountAmount = cartItems.reduce(
      (sum, item) =>
        sum +
        Number(item.unitCost) *
          Number(item.quantity) *
          (Number(item.discount) / 100),
      0,
    )
    return {
      totalAmount: grossTotal,
      discountAmount,
      amountPayable: grossTotal - discountAmount,
    }
  }, [cartItems])

  const handleCustomerTypeChange = async (event) => {
    const type = event.target.value
    setCustomerType(type)
    setCustomerId('')
    setBooks([])
    try {
      setCustomers(await getAllCustomers(type))
    } catch (requestError) {
      setCustomers([])
      setError(requestError.message)
    }
  }

  const handleCustomerChange = async (event) => {
    const selectedCustomerId = event.target.value
    setCustomerId(selectedCustomerId)
    if (!selectedCustomerId) {
      setBooks([])
      return
    }
    try {
      setBooks(await getExerciseOrderBooks(selectedCustomerId))
    } catch (requestError) {
      setBooks([])
      setError(requestError.message)
    }
  }

  const addBookToCart = (book) => {
    const bookId = getBookId(book)
    const stockQuantity = getStockQuantity(book)
    if (stockQuantity <= 0) {
      setError(`${getBookTitle(book)} is out of stock.`)
      return
    }
    setError('')
    setCartItems((current) => {
      const existing = current.find((item) => item.bookId === bookId)
      if (existing) {
        if (existing.quantity + 1 > stockQuantity) {
          setError(
            `Only ${stockQuantity} unit(s) of ${existing.description} in stock.`,
          )
          return current
        }
        const newQty = existing.quantity + 1
        return current.map((item) =>
          item.bookId === bookId
            ? {
                ...item,
                quantity: newQty,
                copies: newQty % 20,
                packs: Math.floor((newQty % 200) / 20),
                cartons: Math.floor(newQty / 200),
              }
            : item,
        )
      }
      return [
        ...current,
        {
          bookId,
          description: getBookTitle(book),
          unitCost: getBookUnitCost(book),
          stockQuantity,
          cartons: 0,
          packs: 0,
          copies: 1,
          quantity: 1,
          discount: 0,
        },
      ]
    })
  }

  const updateCartPackaging = (bookId, packField, value) => {
    setCartItems((current) =>
      current.map((item) => {
        if (item.bookId !== bookId) return item

        const cartons = packField === 'cartons' ? value : item.cartons || 0
        const packs = packField === 'packs' ? value : item.packs || 0
        const copies = packField === 'copies' ? value : item.copies || 0

        const totalQty = cartons * 200 + packs * 20 + copies * 1

        if (totalQty > item.stockQuantity) {
          setError(
            `Only ${item.stockQuantity} unit(s) of ${item.description} in stock.`,
          )
          return item
        }

        return {
          ...item,
          [packField]: value,
          quantity: totalQty,
        }
      }),
    )
  }

  const updateCartUnitCost = (bookId, unitCost) => {
    setCartItems((current) =>
      current.map((item) =>
        item.bookId === bookId ? { ...item, unitCost } : item,
      ),
    )
  }

  const removeCartItem = (bookId) => {
    setCartItems((current) => current.filter((item) => item.bookId !== bookId))
  }

  const openInvoice = (savedOrder) => {
    const data = savedOrder || {}
    if (!data.orderid) {
      setError('Order was placed, but the invoice details could not be loaded.')
      return
    }
    const invoiceNumber =
      getValue(data, ['orderid', 'invoiceNumber', 'id'], '') || Date.now()
    const invoiceData = {
      company: 'Buck Press Limited',
      orderType: 'exercise_book',
      order_type: 'exercise_book',
      customer: {
        name: getValue(data, ['cust_name', 'name', 'company']),
        address: getValue(data, ['address', 'cust_address']),
        phone: getValue(data, ['contact', 'phone', 'cust_contact']),
      },
      invoiceNumber,
      salesExecutive: getValue(data, [
        'sales_executive',
        'user_fullname',
        'fullname',
        'name',
      ]),
      requestingOfficer: getValue(data, [
        'requesting_user',
        'fullname',
        'name',
        'email',
      ]),
      printDate: new Date().toISOString(),
      items: (data.orderBooks || []).map((orderBook) => {
        const quantity = Number(orderBook.quantity) || 0
        return {
          quantity,
          cartons: orderBook.cartons ?? Math.floor(quantity / 200),
          packs: orderBook.packs ?? Math.floor((quantity % 200) / 20),
          copies: orderBook.copies ?? quantity % 20,
          description: orderBook.book_title,
          unitCost: orderBook.unitprice,
          discount: orderBook.discount * 100 > 0 ? orderBook.discount * 100 : 0,
          subtotal: orderBook.subtotal,
          totalReturned: orderBook.totalreturned,
          witholdingTax: data.witholdingTax,
        }
      }),
      summary: {
        grossTotal: totals.totalAmount,
        discountTotal: totals.discountAmount,
        NetTotal: totals.amountPayable,
      },
    }
    openPrintableDocument(`/invoice/${encodeURIComponent(invoiceNumber)}`)
  }

  const placeOrder = async () => {
    if (!customerId) {
      setError('Please select a customer before placing the order.')
      return
    }
    if (!cartItems.length) {
      setError('Add at least one book to the cart before placing the order.')
      return
    }
    if (!loginUserId) {
      setError('Unable to identify the logged-in user. Please sign in again.')
      return
    }
    const today = new Date().toISOString().slice(0, 10)
    if (!orderDate || orderDate > today) {
      setError('Order date cannot be a future date.')
      return
    }
    const unavailableItem = cartItems.find(
      (item) => Number(item.quantity) > Number(item.stockQuantity),
    )
    if (unavailableItem) {
      setError(
        `Only ${unavailableItem.stockQuantity} unit(s) of ${unavailableItem.description} in stock.`,
      )
      return
    }

    setIsSaving(true)
    setError('')
    try {
      if (isEditing) {
        await updateExerciseOrder(orderId, {
          orderid: orderId,
          customerid: customerId,
          userid: parseInt(loginUserId),
          order_date: orderDate,
          order_type: 'exercise_book',
          books: cartItems.map((item) => ({
            bookid: item.bookId,
            quantity: item.quantity,
            unitcost: Number(item.unitCost) || 0,
            discount: item.discount,
            cartons: item.cartons,
            packs: item.packs,
            copies: item.copies,
          })),
        })
        navigate(`/sales/orders/exercise-books/${orderId}`)
      } else {
        const savedOrder = await createExerciseOrder({
          customerid: customerId,
          userid: parseInt(loginUserId),
          order_date: orderDate,
          order_type: 'exercise_book',
          books: cartItems.map((item) => ({
            bookid: item.bookId,
            quantity: item.quantity,
            unitcost: Number(item.unitCost) || 0,
            discount: item.discount,
            cartons: item.cartons,
            packs: item.packs,
            copies: item.copies,
          })),
        })
        setCartItems([])
        openInvoice(savedOrder)
      }
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className='new-order-page'>
      <nav className='new-order-page__breadcrumb' aria-label='Breadcrumb'>
        <Link to='/dashboard'>Dashboard</Link>
        <span aria-hidden='true'>/</span>
        <Link to='/sales/orders/exercise-books'>Exercise orders</Link>
        <span aria-hidden='true'>/</span>
        <strong>{isEditing ? `Edit order ${orderId}` : 'New order'}</strong>
      </nav>
      <header className='new-order-page__header'>
        <span className='app-content__eyebrow'>Sales</span>
        <h1>
          {isEditing
            ? `Edit exercise order ${orderId}`
            : 'Create new exercise order'}
        </h1>
        <p>
          {isEditing
            ? 'Update customer, items, and packaging quantities for this order.'
            : 'Select a customer, add books to the cart, and place the order.'}
        </p>
      </header>
      {error && (
        <div className='new-order-page__error' role='alert'>
          {error}
        </div>
      )}

      <section
        className='new-order-card'
        aria-labelledby='new-order-customer-title'
      >
        <h2 id='new-order-customer-title'>Customer & order details</h2>
        <div className='new-order-form'>
          <label>
            Customer type
            <select
              value={customerType}
              onChange={handleCustomerTypeChange}
              disabled={isSaving}
            >
              <option value=''>All customer types</option>
              {customerTypes.map((type) => (
                <option key={getOptionValue(type)} value={getOptionValue(type)}>
                  {getOptionLabel(type)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Customer
            <select
              value={customerId}
              onChange={handleCustomerChange}
              required
              disabled={isSaving}
            >
              <option value=''>Select a customer</option>
              {customers.map((customer) => (
                <option
                  key={getValue(customer, ['customerid', 'id'])}
                  value={getValue(customer, ['customerid', 'id'])}
                >
                  {getValue(customer, ['cust_name', 'name', 'company'])}
                </option>
              ))}
            </select>
          </label>
          <label>
            Order date
            <input
              type='date'
              value={orderDate}
              onChange={(event) => setOrderDate(event.target.value)}
              required
              disabled={isSaving}
            />
          </label>
        </div>
      </section>

      <section
        className='new-order-books-card'
        aria-labelledby='new-order-books-title'
      >
        <div className='new-order-books-card__header'>
          <h2 id='new-order-books-title'>Available books ({books.length})</h2>
          <input
            className='new-order-books-card__filter'
            type='search'
            placeholder='Filter books by title or author...'
            value={bookFilter}
            onChange={(event) => setBookFilter(event.target.value)}
          />
        </div>
        <div className='new-order-books-grid'>
          {!customerId ? (
            <div className='new-order-books-grid__empty'>
              Please select a customer to view available books.
            </div>
          ) : filteredBooks.length ? (
            filteredBooks.map((book) => (
              <button
                key={getBookId(book)}
                className='new-order-book'
                type='button'
                onClick={() => addBookToCart(book)}
              >
                <span className='new-order-book__title'>
                  {getBookTitle(book)}
                </span>
                <span className='new-order-book__author'>
                  {getBookAuthor(book)}
                </span>
                <span className='new-order-book__cost'>
                  {formatCurrency(getBookUnitCost(book))}
                </span>
                <span className='new-order-book__cart-icon' aria-hidden='true'>
                  <svg viewBox='0 0 24 24'>
                    <path d='M12 5v14M5 12h14' />
                  </svg>
                </span>
              </button>
            ))
          ) : (
            <div className='new-order-books-grid__empty'>
              No books matched the filter.
            </div>
          )}
        </div>
      </section>

      <section
        className='new-order-cart-card'
        aria-labelledby='new-order-cart-title'
      >
        <h2 id='new-order-cart-title'>Cart items ({cartItems.length})</h2>
        <div className='new-order-cart-wrap'>
          <table className='new-order-cart-table'>
            <thead>
              <tr>
                <th>Description</th>
                <th>Unit cost</th>
                <th>Carton (x200)</th>
                <th>Pack (x20)</th>
                <th>Copies (x1)</th>
                <th>Total Qty</th>
                <th>Discount (%)</th>
                <th>Subtotal</th>
                <th>
                  <span className='sr-only'>Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {cartItems.length ? (
                cartItems.map((item) => (
                  <tr key={item.bookId}>
                    <td>
                      <strong>{item.description}</strong>
                    </td>
                    <td>
                      <input
                        type='number'
                        min='0'
                        step='0.01'
                        value={item.unitCost}
                        onChange={(event) =>
                          updateCartUnitCost(
                            item.bookId,
                            Math.max(0, Number(event.target.value)),
                          )
                        }
                        aria-label={`Unit cost for ${item.description}`}
                        disabled={isSaving}
                      />
                    </td>
                    <td>
                      <input
                        type='number'
                        min='0'
                        step='1'
                        value={item.cartons ?? 0}
                        onChange={(event) =>
                          updateCartPackaging(
                            item.bookId,
                            'cartons',
                            Math.max(0, parseInt(event.target.value, 10) || 0),
                          )
                        }
                        disabled={isSaving}
                      />
                    </td>
                    <td>
                      <input
                        type='number'
                        min='0'
                        step='1'
                        value={item.packs ?? 0}
                        onChange={(event) =>
                          updateCartPackaging(
                            item.bookId,
                            'packs',
                            Math.max(0, parseInt(event.target.value, 10) || 0),
                          )
                        }
                        disabled={isSaving}
                      />
                    </td>
                    <td>
                      <input
                        type='number'
                        min='0'
                        step='1'
                        value={item.copies ?? 0}
                        onChange={(event) =>
                          updateCartPackaging(
                            item.bookId,
                            'copies',
                            Math.max(0, parseInt(event.target.value, 10) || 0),
                          )
                        }
                        disabled={isSaving}
                      />
                    </td>
                    <td className='new-order-cart-table__total'>
                      {item.quantity}
                    </td>
                    <td>
                      <input
                        type='number'
                        min='0'
                        max='100'
                        step='0.01'
                        value={item.discount}
                        onChange={(event) =>
                          setCartItems((current) =>
                            current.map((ci) =>
                              ci.bookId === item.bookId
                                ? {
                                    ...ci,
                                    discount: Math.min(
                                      100,
                                      Math.max(
                                        0,
                                        parseFloat(event.target.value) || 0,
                                      ),
                                    ),
                                  }
                                : ci,
                            ),
                          )
                        }
                        disabled={isSaving}
                      />
                    </td>
                    <td className='new-order-cart-table__total'>
                      {formatCurrency(getLineTotal(item))}
                    </td>
                    <td>
                      <button
                        className='new-order-cart-table__remove'
                        type='button'
                        onClick={() => removeCartItem(item.bookId)}
                        disabled={isSaving}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan='6' className='new-order-cart-table__empty'>
                    No books in cart. Click on a book above to add it.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className='new-order-summary' aria-label='Order totals'>
        <div className='new-order-summary__cards'>
          <div className='new-order-summary__card'>
            <span>Gross total</span>
            <strong>{formatCurrency(totals.totalAmount)}</strong>
          </div>
          <div className='new-order-summary__card'>
            <span>Discount amount</span>
            <strong>{formatCurrency(totals.discountAmount)}</strong>
          </div>
          <div className='new-order-summary__card new-order-summary__card--highlight'>
            <span>Net total</span>
            <strong>{formatCurrency(totals.amountPayable)}</strong>
          </div>
        </div>
        <button
          className='new-order-button new-order-button--primary'
          type='button'
          onClick={placeOrder}
          disabled={isSaving || !cartItems.length || !customerId}
        >
          {isSaving
            ? isEditing
              ? 'Updating order...'
              : 'Placing order...'
            : isEditing
              ? 'Update order'
              : 'Place order'}
        </button>
      </section>
    </div>
  )
}

export default NewExerciseOrder
